import { useState } from 'react';

import { MoveToCofeeAction, MoveToCofeeStep } from './constants';
import {
  AddToMemberListFormData,
  QuickCollectFormData,
  QuickCollectGroupFormData,
  ReviewField
} from './types';
import useQuickCollectAmountLimits from './useQuickCollectAmountLimits';
import { isMoveToCofeeNameInvalid } from './validation';

const getInitialMemberListForm = (
  leadName: string
): AddToMemberListFormData => ({
  name: leadName,
  branchId: '',
  branchName: '',
  assignToGroup: false,
  groupId: '',
  groupName: ''
});

const getInitialQuickCollectForm = (
  leadName: string
): QuickCollectFormData => ({
  name: leadName,
  branchId: '',
  branchName: '',
  amount: '',
  notes: ''
});

const getInitialQuickCollectGroupForm = (
  leadName: string
): QuickCollectGroupFormData => ({
  name: leadName,
  branchId: '',
  branchName: '',
  amount: '',
  notes: '',
  groupId: '',
  groupName: ''
});

const isAmountValid = (
  amount: string,
  minAmount: number,
  maxAmount: number
) => {
  const num = Number(amount);
  return num >= minAmount && num <= maxAmount;
};

const isNotesValid = (notes: string) => notes.trim().length >= 3;

const isMemberListValid = (form: AddToMemberListFormData) =>
  !!(!isMoveToCofeeNameInvalid(form.name) && form.branchId);

const isQuickCollectValid = (
  form: QuickCollectFormData,
  minAmount: number,
  maxAmount: number
) =>
  !!(
    !isMoveToCofeeNameInvalid(form.name) &&
    form.branchId &&
    form.amount &&
    isAmountValid(form.amount, minAmount, maxAmount) &&
    form.notes &&
    isNotesValid(form.notes)
  );

const isQuickCollectGroupValid = (
  form: QuickCollectGroupFormData,
  minAmount: number,
  maxAmount: number
) =>
  !!(
    !isMoveToCofeeNameInvalid(form.name) &&
    form.branchId &&
    form.amount &&
    isAmountValid(form.amount, minAmount, maxAmount) &&
    form.notes &&
    isNotesValid(form.notes) &&
    form.groupId
  );

const getMemberListReviewFields = (
  form: AddToMemberListFormData
): ReviewField[] => {
  const fields: ReviewField[] = [
    { label: 'Lead Name', value: form.name },
    { label: 'Branch', value: form.branchName }
  ];
  if (form.assignToGroup && form.groupName) {
    fields.push({ label: 'Group', value: form.groupName });
  }
  return fields;
};

const getQuickCollectReviewFields = (
  form: QuickCollectFormData
): ReviewField[] => [
  { label: 'Lead Name', value: form.name },
  { label: 'Branch', value: form.branchName },
  { label: 'Amount', value: form.amount },
  { label: 'Notes', value: form.notes }
];

const getQuickCollectGroupReviewFields = (
  form: QuickCollectGroupFormData
): ReviewField[] => [
  { label: 'Lead Name', value: form.name },
  { label: 'Branch', value: form.branchName },
  { label: 'Group', value: form.groupName },
  { label: 'Amount', value: form.amount },
  { label: 'Notes', value: form.notes }
];

interface UseMoveToCofeeFormArgs {
  leadName: string;
}

const useMoveToCofeeForm = ({ leadName }: UseMoveToCofeeFormArgs) => {
  const { minAmount, maxAmount } = useQuickCollectAmountLimits();

  const [memberListForm, setMemberListForm] = useState<AddToMemberListFormData>(
    getInitialMemberListForm(leadName)
  );
  const [quickCollectForm, setQuickCollectForm] =
    useState<QuickCollectFormData>(getInitialQuickCollectForm(leadName));
  const [quickCollectGroupForm, setQuickCollectGroupForm] =
    useState<QuickCollectGroupFormData>(
      getInitialQuickCollectGroupForm(leadName)
    );

  const updateMemberListForm = (data: Partial<AddToMemberListFormData>) =>
    setMemberListForm((prev) => ({ ...prev, ...data }));

  const updateQuickCollectForm = (data: Partial<QuickCollectFormData>) =>
    setQuickCollectForm((prev) => ({ ...prev, ...data }));

  const updateQuickCollectGroupForm = (
    data: Partial<QuickCollectGroupFormData>
  ) => setQuickCollectGroupForm((prev) => ({ ...prev, ...data }));

  const isContinueDisabled = (
    currentStep: MoveToCofeeStep,
    selectedAction: MoveToCofeeAction | null
  ) => {
    if (currentStep === MoveToCofeeStep.CHOOSE_ACTION) {
      return !selectedAction;
    }
    if (currentStep === MoveToCofeeStep.CONFIGURE) {
      if (selectedAction === MoveToCofeeAction.ADD_TO_MEMBER_LIST) {
        return !isMemberListValid(memberListForm);
      }
      if (selectedAction === MoveToCofeeAction.QUICK_COLLECT) {
        return !isQuickCollectValid(quickCollectForm, minAmount, maxAmount);
      }
      if (selectedAction === MoveToCofeeAction.QUICK_COLLECT_ADD_TO_GROUP) {
        return !isQuickCollectGroupValid(
          quickCollectGroupForm,
          minAmount,
          maxAmount
        );
      }
    }
    return false;
  };

  const getReviewFields = (
    selectedAction: MoveToCofeeAction | null
  ): ReviewField[] => {
    if (selectedAction === MoveToCofeeAction.ADD_TO_MEMBER_LIST) {
      return getMemberListReviewFields(memberListForm);
    }
    if (selectedAction === MoveToCofeeAction.QUICK_COLLECT) {
      return getQuickCollectReviewFields(quickCollectForm);
    }
    if (selectedAction === MoveToCofeeAction.QUICK_COLLECT_ADD_TO_GROUP) {
      return getQuickCollectGroupReviewFields(quickCollectGroupForm);
    }
    return [];
  };

  return {
    memberListForm,
    quickCollectForm,
    quickCollectGroupForm,
    updateMemberListForm,
    updateQuickCollectForm,
    updateQuickCollectGroupForm,
    isContinueDisabled,
    getReviewFields
  };
};

export default useMoveToCofeeForm;
